import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

const CheckIcon = RESIDENTS_SVG_COMPONENTS.CHECK;

type SelectionType = 'radio' | 'checkbox';

interface SelectableOptionProps {
  label: string;
  isSelected: boolean;
  onPress: () => void;
  type?: SelectionType;
}

/**
 * Shared selectable option component for radio buttons and checkboxes
 * Used in StepPriceCondition (radio) and StepPickupPreferences (checkbox)
 */
export const SelectableOption: React.FC<SelectableOptionProps> = ({
  label,
  isSelected,
  onPress,
  type = 'checkbox',
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const isRadio = type === 'radio';

  return (
    <TouchableOpacity
      style={[styles.optionItem, { backgroundColor: MARKETPLACE_COLORS.optionItemBackground }]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <Text style={[
        styles.optionText,
        { color: MARKETPLACE_COLORS.textPrimary },
        isSelected && styles.optionTextSelected,
      ]}>
        {label}
      </Text>
      <View style={styles.selectionContainer}>
        <View style={[
          styles.selectionOuter,
          {
            borderColor: MARKETPLACE_COLORS.checkboxBorder,
            backgroundColor: MARKETPLACE_COLORS.checkboxBackground,
          },
          isRadio ? styles.radio : styles.checkbox,
          isSelected && {
            backgroundColor: MARKETPLACE_COLORS.checkboxSelected,
            borderColor: MARKETPLACE_COLORS.checkboxSelected,
          },
        ]}>
          {isSelected && (
            isRadio ? (
              <View style={[styles.radioInner, { backgroundColor: MARKETPLACE_COLORS.radioInner }]} />
            ) : (
              <CheckIcon width={12} height={12} color={MARKETPLACE_COLORS.textPrimary} />
            )
          )}
        </View>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  optionItem: {
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  optionText: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 21,
  },
  optionTextSelected: {
    fontWeight: '500',
  },
  selectionContainer: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectionOuter: {
    width: 16,
    height: 16,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radio: {
    borderRadius: 8, // Full circle for radio
  },
  checkbox: {
    borderRadius: 4, // Rounded corners for checkbox
  },
  radioInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
});
